/**
 * Имя в отведённую ширину: сначала уменьшаем кегль, потом обрезаем.
 *
 * Порядок именно такой: уменьшать до нечитаемого нельзя, а обрезать имя,
 * которое влезло бы на кегль поменьше, — обидно. Отсюда пол по кеглю и
 * многоточие уже за ним.
 *
 * Ширина МЕРЯЕТСЯ, а не считается по числу букв. SVG сам текст не переносит
 * и не обрезает, а getComputedTextLength требует, чтобы элемент уже был
 * в документе, — поэтому мерит canvas, тем же шрифтом, каким имя нарисуют.
 * Счёт по буквам разъезжается с правдой вдвое: у Nunito 800 «Константин»
 * стоит 0,58 кегля за букву, а «Вы» — 0,75, и короткие широкие имена
 * вылезали за двор. На телефоне хуже: если Nunito не доехал, iOS подставляет
 * SF Rounded, а он ещё шире — под какой из них подбирать коэффициент,
 * заранее неизвестно.
 */

/**
 * С какого кегля начинаем и ниже какого не опускаемся, в пикселях.
 *
 * Пиксели тут — при доске шириной 360 (телефон), см. inCells в BoardView:
 * на широком экране доска больше, и буквы растут вместе с ней. То есть это
 * ровно тот размер, на который жалуются, — телефонный.
 *
 * Экспортируются, чтобы тесты сверялись с ними, а не с числами, набранными
 * второй раз: прошлый раз кегль правили здесь, а в тестах остались 14 и 10.
 *
 * Обрезку решает только ПОЛ, а не потолок: fitName спускается от START к
 * FLOOR и режет лишь то, что не влезло и на полу. Значит потолок можно
 * поднимать свободно — он достаётся коротким именам, а длинные всё равно
 * доедут до пола целыми. Двадцать — это кегль основного текста игры на
 * телефоне: имя за столом не должно быть мельче подписи в меню.
 *
 * А вот пол — тонкое место, и одиннадцать здесь не с потолка: ширина двора
 * одна (CARD.room, 80), и «Константин» — десять букв — на двенадцати уже не
 * влезает и уходит под многоточие. Поднимать пол значит обрезать имена;
 * растить их дальше можно только в две строки.
 */
export const START = 20
export const FLOOR = 11

/**
 * Средняя ширина буквы в долях кегля — на случай, когда мерить нечем.
 *
 * Нечем в jsdom, где идут тесты (см. `canvas` ниже). С запасом относительно
 * Nunito: лучше обрезать лишнее, чем вылезти за двор.
 */
const GLYPH = 0.68

let pad: CanvasRenderingContext2D | null | undefined
let face: string | undefined

/**
 * Шрифт карточки — тот же, что в стилях (`--display` у `.board__card`).
 *
 * Читается из CSS, а не выписан здесь второй раз: разойтись с ним значит
 * мерить не тем, чем рисуем.
 */
function font(size: number): string {
  if (face === undefined) {
    face = getComputedStyle(document.documentElement).getPropertyValue('--display').trim()
  }

  return `800 ${size}px ${face === '' ? 'sans-serif' : face}`
}

/**
 * Канва, которой меряем, — или null, когда мерить нечем.
 *
 * Нечем в jsdom, где идут тесты. Отличаем его по FontFaceSet: его там нет
 * вовсе, а canvas есть — но пустой, и вызов getContext печатает
 * «Not implemented» в каждый прогон.
 */
function canvas(): CanvasRenderingContext2D | null {
  if (pad === undefined) {
    pad = document.fonts === undefined ? null : document.createElement('canvas').getContext('2d')
  }

  return pad
}

/** Ширина строки данным кеглем в пикселях. */
export function textWidth(text: string, size: number): number {
  const at = canvas()

  if (at === null) return text.length * size * GLYPH

  at.font = font(size)

  return at.measureText(text).width
}

export function fitName(name: string, room: number): { text: string; size: number } {
  const text = name.trim()
  let size = START

  while (size > FLOOR && textWidth(text, size) > room) size -= 0.5

  if (textWidth(text, size) <= room) return { text, size }

  /*
   * За полом кегля остаётся обрезка. Буквы разной ширины, поэтому длина
   * подбирается измерением с конца, а не делением ширины на среднюю букву:
   * «Александра-Мария» и «ЖЖЖЖЖЖЖЖ» одной длины занимают разное место.
   */
  let fits = text.length

  while (fits > 1 && textWidth(`${text.slice(0, fits).trimEnd()}…`, size) > room) fits -= 1

  return { text: `${text.slice(0, fits).trimEnd()}…`, size }
}
